// Projects tab: all projects across customers.
// Part of the Reference page (components/Reference.jsx). Split out 2026-10-14.
import { useState, useEffect, Fragment } from 'react';
import { useApi, postJson, fetchJson } from '../../api.js';

// ProjectsTab — ALL projects across customers, own Reference sub-tab
// (moved out of the customer card per Suresh 2026-08-20). A tip
// assigned to a project bills at the project rate (per-tier fallback
// to the customer default when a project rate is blank). Assignment
// happens on the Review tab; this tab manages the projects themselves.
// Rate changes are retroactive for all assigned tips.
export default function ProjectsTab({ canWrite = true }) {
  // Data: all projects (any customer), the add/edit form, filters, and the expanded row's tips.
  const [rows, setRows] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  // Drill-down: expanded project id -> { loading, tips }
  const [expandedId, setExpandedId] = useState(null);
  const [expandedTips, setExpandedTips] = useState(null);
  // Drill-down: click a project to list the tips billed to it.
  async function toggleExpand(p) {
    if (expandedId === p.id) { setExpandedId(null); setExpandedTips(null); return; }
    setExpandedId(p.id); setExpandedTips(null);
    try {
      const r = await fetchJson('/api/customer-projects?tips_for=' + p.id);
      setExpandedTips(r.tips || []);
    } catch (e) { setExpandedTips({ error: e.message }); }
  }
  // Customer names for the project form's customer picker.
  const customersApi = useApi('/api/customers');
  const customerNames = ((customersApi.data && customersApi.data.customers) || [])
    .map(c => c.display_name).filter(Boolean).sort((a, b) => a.localeCompare(b));
  const RATE_FIELDS = [
    ['small_rate', 'Small'], ['regular_rate', 'Regular'],
    ['oversize18_rate', '18m³'], ['oversize28_rate', '28m³'],
    ['drill_mud_rate', 'DM'], ['drill_mud_18_rate', 'DM-18'], ['drill_mud_28_rate', 'DM-28']
  ];
  // Actions: load, blank form, save, activate / deactivate.
  async function load() {
    try {
      const r = await fetchJson('/api/customer-projects');
      setRows(r.projects || []);
    } catch (e) { setRows([]); }
  }
  useEffect(() => { load(); }, []);
  function blankProject() {
    return { customer:'', name:'', code:'', small_rate:'', regular_rate:'', oversize18_rate:'', oversize28_rate:'',
             drill_mud_rate:'', drill_mud_18_rate:'', drill_mud_28_rate:'', active:true,
             effective_from:'', effective_to:'', notes:'' };
  }
  async function saveRow() {
    if (!editRow || !editRow.name || !editRow.name.trim()) { alert('Project name is required.'); return; }
    if (!editRow.id && (!editRow.customer || !editRow.customer.trim())) { alert('Customer is required.'); return; }
    setBusy(true);
    try {
      if (editRow.id) {
        const body = {};
        for (const [f] of RATE_FIELDS) body[f] = editRow[f];
        body.name = editRow.name; body.code = editRow.code || null;
        body.active = editRow.active !== false;
        body.effective_from = editRow.effective_from || null;
        body.effective_to   = editRow.effective_to   || null;
        body.notes = editRow.notes || null;
        const r = await fetch('/api/customer-projects/' + editRow.id, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify(body)
        });
        if (!r.ok) throw new Error(r.status + ' ' + (await r.text()));
      } else {
        await postJson('/api/customer-projects', { ...editRow });
      }
      setEditRow(null); await load();
    } catch (e) { alert('Save failed: ' + e.message); }
    finally { setBusy(false); }
  }
  async function toggleActive(p) {
    setBusy(true);
    try {
      const r = await fetch('/api/customer-projects/' + p.id, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ active: !(p.active !== false) })
      });
      if (!r.ok) throw new Error(r.status + ' ' + (await r.text()));
      await load();
    } catch (e) { alert('Update failed: ' + e.message); }
    finally { setBusy(false); }
  }
  // Visible list after filter + "show inactive".
  const listAll = rows || [];
  const f = filter.trim().toLowerCase();
  const list = listAll
    .filter(p => showInactive || p.active !== false)
    .filter(p => !f
      || String(p.name || '').toLowerCase().includes(f)
      || String(p.code || '').toLowerCase().includes(f)
      || String(p.customer_canonical_name || '').toLowerCase().includes(f));
  const fmtRate = v => (v == null || v === '') ? '—' : '$' + Number(v).toLocaleString();
  // Screen: toolbar, add/edit form, projects table with drill-down.
  return (
    <div className="card" style={{ padding: '0.75rem' }}>
      <div style={{display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.5rem'}}>
        <input type="text" placeholder="Filter by project, code, or customer…" value={filter} onChange={e=>setFilter(e.target.value)}
               style={{ flex:1, padding:'4px 8px' }} />
        <label className="small" style={{ display:'flex', alignItems:'center', gap:4, color:'#666', whiteSpace:'nowrap' }}>
          <input type="checkbox" checked={showInactive} onChange={e=>setShowInactive(e.target.checked)} /> show inactive
        </label>
        {canWrite && (
          <button className="refresh-btn" style={{background:'#7c3aed'}}
                  onClick={() => setEditRow(blankProject())}
                  disabled={!!editRow}>+ Add project</button>
        )}
        <span className="small" style={{color:'#666'}}>{list.length}/{listAll.length} projects</span>
      </div>
      {/* Add / edit form (rates here override the customer's rates for tips tagged to this project). */}
      {editRow && (
        <div style={{marginTop:'0.4rem', padding:'0.6rem', background:'#faf5ff', border:'1px solid #e9d5ff', borderRadius:4, fontSize:'0.87em'}}>
          <div style={{display:'grid', gridTemplateColumns:'1.5fr 1.5fr 1fr', gap:'0.4rem 0.6rem'}}>
            <label>Customer<br/>
              {editRow.id
                ? <input value={editRow.customer_canonical_name || ''} disabled style={{width:'100%', boxSizing:'border-box', background:'#f1f5f9'}} />
                : <>
                    <input list="project-customer-options" value={editRow.customer||''}
                           onChange={e=>setEditRow({...editRow, customer:e.target.value})}
                           placeholder="pick a customer" style={{width:'100%', boxSizing:'border-box'}} />
                    <datalist id="project-customer-options">{customerNames.map(n => <option key={n} value={n} />)}</datalist>
                  </>}
            </label>
            <label>Project name<br/>
              <input value={editRow.name||''} onChange={e=>setEditRow({...editRow, name:e.target.value})} placeholder="e.g. West Toronto Trunk Sewer" style={{width:'100%', boxSizing:'border-box'}} /></label>
            <label>Code (optional)<br/>
              <input value={editRow.code||''} onChange={e=>setEditRow({...editRow, code:e.target.value})} placeholder="WTTS-26" style={{width:'100%', boxSizing:'border-box'}} /></label>
          </div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:'0.3rem 0.5rem', marginTop:'0.4rem'}}>
            {RATE_FIELDS.map(([f, label]) => (
              <label key={f} className="small">{label}<br/>
                <input type="number" step="0.01" value={editRow[f] ?? ''} onChange={e=>setEditRow({...editRow, [f]:e.target.value})}
                       placeholder="—" style={{width:'100%', boxSizing:'border-box'}} /></label>
            ))}
          </div>
          <div className="small" style={{color:'#7e22ce', marginTop:'0.2rem'}}>
            Blank tier = falls back to the customer's default rate for that tier. Rate changes re-price every tip assigned to this project (closed cycles stay frozen).
          </div>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 2fr', gap:'0.4rem 0.6rem', marginTop:'0.4rem'}}>
            <label className="small">Effective from (optional)<br/>
              <input type="date" value={editRow.effective_from ? String(editRow.effective_from).slice(0,10) : ''} onChange={e=>setEditRow({...editRow, effective_from:e.target.value})} style={{width:'100%', boxSizing:'border-box'}} /></label>
            <label className="small">Effective to (optional)<br/>
              <input type="date" value={editRow.effective_to ? String(editRow.effective_to).slice(0,10) : ''} onChange={e=>setEditRow({...editRow, effective_to:e.target.value})} style={{width:'100%', boxSizing:'border-box'}} /></label>
            <label className="small">Notes<br/>
              <input value={editRow.notes||''} onChange={e=>setEditRow({...editRow, notes:e.target.value})} style={{width:'100%', boxSizing:'border-box'}} /></label>
          </div>
          <div style={{display:'flex', justifyContent:'flex-end', gap:'0.4rem', marginTop:'0.5rem'}}>
            <button className="refresh-btn small" onClick={() => setEditRow(null)} disabled={busy} style={{background:'#94a3b8'}}>Cancel</button>
            <button className="refresh-btn small" onClick={saveRow} disabled={busy} style={{background:'#7c3aed'}}>{editRow.id ? 'Update' : 'Create'}</button>
          </div>
        </div>
      )}
      {/* Projects table; clicking a row expands its tips underneath. */}
      {list.length > 0 && (
        <div style={{marginTop:'0.3rem', fontSize:'0.85em'}}>
          <table className="datatable" style={{margin:0}}>
            <thead><tr>
              <th>Customer</th><th>Project</th><th>Code</th>
              <th className="num">Small</th><th className="num">Regular</th><th className="num">18m³</th><th className="num">28m³</th>
              <th className="num">DM</th><th className="num">DM-18</th><th className="num">DM-28</th>
              <th>Window</th><th className="num">Active</th><th style={{width:'8em'}}></th>
            </tr></thead>
            <tbody>
              {list.map(p => (<Fragment key={p.id}>
                <tr style={{opacity: p.active === false ? 0.5 : 1}}>
                  <td>{p.customer_canonical_name}</td>
                  <td>
                    <button className="link" onClick={() => toggleExpand(p)}
                            title="Show the tips assigned to this project"
                            style={{ background:'none', border:'none', cursor:'pointer', padding:0, color:'#7c3aed', fontWeight:600 }}>
                      {expandedId === p.id ? '▾ ' : '▸ '}{p.name}
                    </button>
                  </td>
                  <td className="small">{p.code || ''}</td>
                  <td className="num">{fmtRate(p.small_rate)}</td>
                  <td className="num">{fmtRate(p.regular_rate)}</td>
                  <td className="num">{fmtRate(p.oversize18_rate)}</td>
                  <td className="num">{fmtRate(p.oversize28_rate)}</td>
                  <td className="num">{fmtRate(p.drill_mud_rate)}</td>
                  <td className="num">{fmtRate(p.drill_mud_18_rate)}</td>
                  <td className="num">{fmtRate(p.drill_mud_28_rate)}</td>
                  <td className="small">{(p.effective_from ? String(p.effective_from).slice(0,10) : '…') + ' → ' + (p.effective_to ? String(p.effective_to).slice(0,10) : '…')}</td>
                  <td className="num">{p.active === false ? '' : '✓'}</td>
                  <td>
                    {canWrite && (<>
                    <button className="refresh-btn small" onClick={() => setEditRow({ ...p })} disabled={!!editRow || busy}>Edit</button>
                    <button className="refresh-btn small" style={{background: p.active === false ? '#15803d' : '#b45309', marginLeft:4}}
                            onClick={() => toggleActive(p)} disabled={!!editRow || busy}
                            title={p.active === false ? 'Reactivate' : 'Deactivate (keeps history; hides from assignment dropdowns)'}>
                      {p.active === false ? 'Activate' : 'Deactivate'}
                    </button>
                    </>)}
                  </td>
                </tr>
                {expandedId === p.id && (
                  <tr>
                    <td colSpan={13} style={{ background:'#faf5ff', padding:'0.5rem 1rem' }}>
                      {expandedTips === null && <span className="small" style={{color:'#666'}}>Loading tips…</span>}
                      {expandedTips && expandedTips.error && <span className="small" style={{color:'#991b1b'}}>Failed: {expandedTips.error}</span>}
                      {Array.isArray(expandedTips) && expandedTips.length === 0 && (
                        <span className="small" style={{color:'#666'}}>No tips assigned to this project yet. Assign them from the Review tab (Project column or bulk-select).</span>
                      )}
                      {Array.isArray(expandedTips) && expandedTips.length > 0 && (
                        <>
                          <div className="small" style={{fontWeight:600, color:'#6b21a8', marginBottom:'0.3rem'}}>
                            {expandedTips.length} tip{expandedTips.length === 1 ? '' : 's'} assigned
                          </div>
                          <table className="datatable" style={{margin:0, fontSize:'0.95em'}}>
                            <thead><tr><th>Date</th><th>Time</th><th>Plate</th><th>Customer</th><th className="num">Tank</th><th className="num">Fill %</th><th>Site</th><th>Status</th></tr></thead>
                            <tbody>
                              {expandedTips.map(t => (
                                <tr key={t.id}>
                                  <td>{t.date ? String(t.date).slice(0,10) : ''}</td>
                                  <td className="small">{t.arrival_time || ''}</td>
                                  <td><code>{t.license_plate || ''}</code></td>
                                  <td>{t.customer || ''}</td>
                                  <td className="num">{t.tank_size ?? ''}</td>
                                  <td className="num">{t.pct_filled ?? ''}</td>
                                  <td className="small">{t.site || ''}</td>
                                  <td className="small">{t.bucket}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
